import React from 'react';
import { Shield, AlertTriangle, Activity, CheckCircle, Terminal } from 'lucide-react';

interface ThreatStatsProps {
    totalItems: number;
    criticalItems: number;
    newItems: number;
    resolvedItems: number;
    isLoading?: boolean;
}

const ThreatStats: React.FC<ThreatStatsProps> = ({
    totalItems,
    criticalItems,
    newItems,
    resolvedItems,
    isLoading = false
}) => {
    if (isLoading) {
        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 font-mono">
                {[...Array(4)].map((_, index) => (
                    <div
                        key={index}
                        className="bg-[#050505] border border-[#1A1D24] rounded-xl p-5 animate-pulse shadow-[0_0_20px_rgba(0,0,0,0.5)]"
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div className="h-4 bg-[#1A1D24] rounded w-1/2"></div>
                            <div className="h-8 w-8 bg-[#1A1D24] rounded-lg"></div>
                        </div>
                        <div className="h-8 bg-[#1A1D24] rounded w-1/3 mt-2"></div>
                    </div>
                ))}
            </div>
        );
    }

    // Calculate resolution rate
    const resolutionRate = totalItems > 0 ? Math.round((resolvedItems / totalItems) * 100) : 0;
    const pendingItems = totalItems - resolvedItems;

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 font-mono">

            {/* Total Threats Card */}
            <div className="bg-[#050505] border border-[#1A1D24] border-t-2 border-t-[#00FFFF] rounded-xl p-5 shadow-[0_0_20px_rgba(0,0,0,0.5)] relative overflow-hidden group hover:bg-[#0A0B0E] transition-colors">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                            Total Threats
                        </div>
                        <div className="text-3xl font-bold text-[#00FFFF] tracking-tight">
                            {totalItems}
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-[#00FFFF]/10 border border-[#00FFFF]/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Shield className="h-5 w-5 text-[#00FFFF]" />
                    </div>
                </div>
            </div>

            {/* Critical Threats Card */}
            <div className="bg-[#050505] border border-[#1A1D24] border-t-2 border-t-[#FF3333] rounded-xl p-5 shadow-[0_0_20px_rgba(0,0,0,0.5)] relative overflow-hidden group hover:bg-[#0A0B0E] transition-colors">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                            Critical Priority
                        </div>
                        <div className="text-3xl font-bold text-[#FF3333] tracking-tight">
                            {criticalItems}
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-[#FF3333]/10 border border-[#FF3333]/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <AlertTriangle className="h-5 w-5 text-[#FF3333]" />
                    </div>
                </div>
                {criticalItems > 0 && (
                    <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#FF3333] animate-pulse opacity-50"></div>
                )}
            </div>

            {/* New / Active Card */}
            <div className="bg-[#050505] border border-[#1A1D24] border-t-2 border-t-[#4da760] rounded-xl p-5 shadow-[0_0_20px_rgba(0,0,0,0.5)] relative overflow-hidden group hover:bg-[#0A0B0E] transition-colors">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                            Active Intercepts
                        </div>
                        <div className="text-3xl font-bold text-[#4da760] tracking-tight">
                            {newItems}
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-[#4da760]/10 border border-[#4da760]/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Activity className="h-5 w-5 text-[#4da760]" />
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-1.5">
                    <Terminal className="h-3 w-3 text-slate-600" />
                    <span className="text-[10px] text-slate-400 uppercase tracking-widest">
                        <strong className="text-[#4da760]">{pendingItems}</strong> Pending Review
                    </span>
                </div>
            </div>

            {/* Resolved Card */}
            <div className="bg-[#050505] border border-[#1A1D24] border-t-2 border-t-[#50A970] rounded-xl p-5 shadow-[0_0_20px_rgba(0,0,0,0.5)] relative overflow-hidden group hover:bg-[#0A0B0E] transition-colors">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">
                            Resolved Vectors
                        </div>
                        <div className="text-3xl font-bold text-[#50A970] tracking-tight">
                            {resolvedItems}
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-[#50A970]/10 border border-[#50A970]/30 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                        <CheckCircle className="h-5 w-5 text-[#50A970]" />
                    </div>
                </div>
                <div className="mt-3 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-[#1A1D24] rounded-full overflow-hidden">
                        <div
                            className="h-full bg-[#50A970] transition-all duration-1000 ease-out"
                            style={{ width: `${resolutionRate}%` }}
                        />
                    </div>
                    <span className="text-[10px] font-bold text-[#50A970] tracking-widest">
                        {resolutionRate}%
                    </span>
                </div>
            </div>

        </div>
    );
};

export default ThreatStats;